import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  CASE_DOCUMENT_STATUS,
  CASE_FILE_KIND,
  CASE_STATUS,
  CaseDocumentFileModel,
  CaseMedicalField,
} from "@shared/models";
import { MODAL_TYPES } from "@shared/constants";
import { showModal } from "@shared/store/actions";
import { selectors } from "@containers/Cases/store";
import { selectors as authSelectors } from "@containers/Auth/store";
import { getCaseFileViewUrl, retryCaseDocument, verifyMedicalField } from "@containers/Cases/store/actions";
import { CASE_DOCUMENT_MODAL_STAGE, UploadedCaseFileKind } from "@containers/Cases/constants";
import {
  getFileTypeLabel,
  isDownloadOnlyFile,
} from "@containers/Cases/components/CaseViewContainer/CaseDocuments/CaseDocuments.utils";

import { CaseDocumentActions } from "../interface";

const UPLOAD_MODAL_TYPES: Record<UploadedCaseFileKind, MODAL_TYPES> = {
  [CASE_FILE_KIND.MEDICAL]: MODAL_TYPES.UPLOAD_MEDICAL_RECORDS,
  [CASE_FILE_KIND.NOTES]: MODAL_TYPES.UPLOAD_CASE_NOTES,
};

const isUploadedKind = (kind: CASE_FILE_KIND): kind is UploadedCaseFileKind => kind in UPLOAD_MODAL_TYPES;

const useCaseDocumentActions = (caseId?: number): CaseDocumentActions => {
  const dispatch = useDispatch();
  const caseStatus = useSelector(selectors.getCase())?.status;
  const caseDocuments = useSelector(selectors.getCaseDocuments());
  // Changing case documents needs the update_case permission, which only admins have.
  const canChangeDocuments = !!useSelector(authSelectors.isAdmin());

  const view = useCallback(
    (file: CaseDocumentFileModel, page?: number) => {
      if (!caseId) return;

      const isDownload = isDownloadOnlyFile(file);
      // Opened on the click itself: browsers block a tab opened later, once the url arrives.
      const tab = isDownload ? null : window.open("", "_blank");
      // Chrome and Firefox open a PDF at this page; Safari ignores it and opens the first page.
      const pageHash = page && getFileTypeLabel(file) === "PDF" ? `#page=${page}` : "";

      dispatch(
        getCaseFileViewUrl.request({
          caseId,
          fileId: file.id,
          cb: (signedUrl) => {
            const url = signedUrl && `${signedUrl}${pageHash}`;

            if (!url) tab?.close();
            else if (tab) tab.location.href = url;
            else if (isDownload) window.location.assign(url);
            else window.open(url, "_blank");
          },
        }),
      );
    },
    [caseId, dispatch],
  );

  const openUploadModal = useCallback(
    (kind: UploadedCaseFileKind, initialStage: CASE_DOCUMENT_MODAL_STAGE, replace = false) => {
      if (!caseId) return;

      dispatch(
        showModal({
          type: UPLOAD_MODAL_TYPES[kind],
          size: "uploadPopup",
          closeOnOverlayClick: false,
          modalContentProps: { maxW: "780px" },
          props: { caseId, initialStage, replace },
        }),
      );
    },
    [caseId, dispatch],
  );

  // Documents are added, replaced and removed only once the case's PSR is processed.
  const isPsrProcessed = caseStatus === CASE_STATUS.SUCCESS;
  const canEditDocuments = canChangeDocuments && isPsrProcessed;

  const add = useMemo(
    () =>
      canEditDocuments
        ? {
            [CASE_FILE_KIND.MEDICAL]: () => openUploadModal(CASE_FILE_KIND.MEDICAL, CASE_DOCUMENT_MODAL_STAGE.UPLOAD),
            [CASE_FILE_KIND.NOTES]: () => openUploadModal(CASE_FILE_KIND.NOTES, CASE_DOCUMENT_MODAL_STAGE.UPLOAD),
          }
        : undefined,
    [canEditDocuments, openUploadModal],
  );

  const replace = useCallback(
    (kind: CASE_FILE_KIND) => {
      if (isUploadedKind(kind)) openUploadModal(kind, CASE_DOCUMENT_MODAL_STAGE.UPLOAD, true);
    },
    [openUploadModal],
  );

  const remove = useCallback(
    (kind: CASE_FILE_KIND) => {
      if (!caseId || !isUploadedKind(kind)) return;

      dispatch(
        showModal({
          type: MODAL_TYPES.REMOVE_CASE_DOCUMENT,
          size: "uploadPopup",
          closeOnOverlayClick: false,
          modalContentProps: { maxW: "540px" },
          props: { caseId, kind },
        }),
      );
    },
    [caseId, dispatch],
  );

  const chooseValues = useCallback(
    (field?: CaseMedicalField) => {
      if (!caseId) return;

      dispatch(
        showModal({
          type: MODAL_TYPES.CHOOSE_FIELD_VALUES,
          size: "uploadPopup",
          closeOnOverlayClick: false,
          modalContentProps: { maxW: "780px" },
          props: { caseId, field },
        }),
      );
    },
    [caseId, dispatch],
  );

  // A parsing replaces the pending choices and flags, so choosing and verifying wait for it.
  const isMedicalParsing = caseDocuments.some(
    ({ kind, status }) => kind === CASE_FILE_KIND.MEDICAL && status === CASE_DOCUMENT_STATUS.PARSING,
  );

  const verify = useCallback(
    (field: CaseMedicalField, onError: (message: string) => void) => {
      if (caseId) dispatch(verifyMedicalField({ caseId, field, onError }));
    },
    [caseId, dispatch],
  );

  const retry = useCallback(
    (kind: CASE_FILE_KIND) => {
      if (caseId) dispatch(retryCaseDocument.request({ caseId, kind }));
    },
    [caseId, dispatch],
  );

  const showResult = useCallback(
    (kind: CASE_FILE_KIND) => {
      if (isUploadedKind(kind)) openUploadModal(kind, CASE_DOCUMENT_MODAL_STAGE.STATUS);
    },
    [openUploadModal],
  );

  return useMemo(
    () => ({
      view,
      add,
      addHint: canChangeDocuments && !isPsrProcessed ? "Available once the PSR is processed" : undefined,
      replace: canEditDocuments ? replace : undefined,
      remove: canEditDocuments ? remove : undefined,
      retry: canChangeDocuments ? retry : undefined,
      showResult,
      chooseValues: canEditDocuments && !isMedicalParsing ? chooseValues : undefined,
      // Who may verify is open question 1; until then it's who may edit the case.
      verify: canEditDocuments && !isMedicalParsing ? verify : undefined,
    }),
    [
      view,
      add,
      canChangeDocuments,
      isPsrProcessed,
      canEditDocuments,
      replace,
      remove,
      retry,
      showResult,
      isMedicalParsing,
      chooseValues,
      verify,
    ],
  );
};

export default useCaseDocumentActions;
